import { io } from 'socket.io-client'
import config from '@/config'
import { useUserStore } from '@/stores/user'
import { getCurrentNode, reportFailure, reportSuccess } from '@/utils/serverSelector'

let socket = null
let reconnectAttempts = 0
let maxReconnectAttempts = 5
let reconnectTimer = null
let isManualDisconnect = false
let heartbeatInterval = null // 心跳定时器
let _nodeSwitched = false    // 是否刚刚切换过节点

// 监听节点切换事件，自动用新地址重连
window.addEventListener('server:switched', () => {
  console.log('[Socket] 检测到服务器切换，自动重连 WebSocket...')
  _nodeSwitched = true
  reconnectAttempts = 0
  if (socket) {
    socket.disconnect()
    stopHeartbeat()
  }
  setTimeout(() => initSocket(), 500)
})

// 初始化 Socket 连接
export function initSocket() {
  const userStore = useUserStore()
  
  if (socket && socket.connected) {
    return socket
  }
  
  if (!userStore.token) {
    console.warn('未登录，无法建立WebSocket连接')
    return null
  }
  
  // 重置重连状态
  reconnectAttempts = 0
  isManualDisconnect = false
  
  // 如果已有socket实例，先断开
  if (socket) {
    console.log('[Socket初始化] 发现已有Socket实例，先断开旧连接')
    // 注意：不要使用 removeAllListeners()，因为会移除所有监听器包括心跳
    // 只移除特定的事件监听器，或者直接断开连接
    socket.disconnect()
    // 停止旧的心跳
    stopHeartbeat()
  }
  
  socket = io(getCurrentNode().ws, {
    auth: {
      token: userStore.token
    },
    transports: ['websocket', 'polling'],
    reconnection: false, // 禁用自动重连，手动控制
    reconnectionDelay: 1000,
    reconnectionAttempts: maxReconnectAttempts
  })
  
  // 连接成功
  socket.on('connect', () => {
    console.log('Socket 连接成功:', socket.id)
    reconnectAttempts = 0 // 重置重连次数
    _nodeSwitched = false
    reportSuccess()
    
    // 发送用户认证
    socket.emit('user:authenticate', {
      token: userStore.token
    })
    
    // 注意：心跳在用户认证成功后才启动（见下面的 user:authenticated 事件）
    
    // 触发连接成功事件
    window.dispatchEvent(new CustomEvent('socket:connected'))
  })
  
  // 用户认证成功
  socket.on('user:authenticated', (data) => {
    console.log('用户认证成功:', data)
    // 认证成功后才启动心跳（每10秒发送一次）
    // console.log('[心跳] ✅ 用户认证成功，准备启动心跳机制')
    // console.log('[心跳] 当前 Socket 状态:', { connected: socket.connected, id: socket.id })
    startHeartbeat()
    
    // 如果是重连成功（之前有重连尝试），触发重连成功事件
    if (reconnectAttempts > 0) {
      console.log(`[重连] 重连成功，重置重连次数: ${reconnectAttempts} -> 0`)
      reconnectAttempts = 0
      
      // 触发重连成功事件，通知UI刷新
      window.dispatchEvent(new CustomEvent('socket:reconnected', {
        detail: { 
          message: '连接已恢复',
          timestamp: Date.now()
        }
      }))
    }
  })
  
  // 用户认证失败
  socket.on('user:auth_error', (data) => {
    console.error('用户认证失败:', data)
    // 认证失败，停止心跳
    // console.log('[心跳] 用户认证失败，停止心跳机制')
    stopHeartbeat()
  })
  
  // 接收跑马灯消息
  socket.on('marquee', (encoded) => {
    try {
      // 解码Base64消息（浏览器兼容方式）
      let decoded
      if (typeof encoded === 'string') {
        try {
          // 使用 atob 解码 Base64（返回 Latin-1 字符串）
          const binaryString = atob(encoded)
          
          // 将 Latin-1 字符串转换为字节数组
          const bytes = new Uint8Array(binaryString.length)
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i)
          }
          
          // 使用 TextDecoder 将字节数组解码为 UTF-8 字符串
          const decoder = new TextDecoder('utf-8')
          const utf8String = decoder.decode(bytes)
          
          try {
            decoded = JSON.parse(utf8String)
          } catch (parseError) {
            // 不是JSON，使用字符串
            decoded = utf8String
          }
        } catch (error) {
          console.error('Base64解码失败:', error)
          // 如果解码失败，尝试直接使用原值
          decoded = encoded
        }
      } else {
        decoded = encoded
      }
      
      // 触发跑马灯事件
      window.dispatchEvent(new CustomEvent('marquee:update', { 
        detail: { marquee: decoded } 
      }))
      console.log('收到跑马灯消息:', decoded)
    } catch (error) {
      console.error('解析跑马灯消息失败:', error)
    }
  })
  
  // 连接断开
  socket.on('disconnect', (reason) => {
    console.log('Socket 连接断开:', reason)
    // console.log(`[心跳] 连接断开，停止心跳机制，原因: ${reason}`)
    
    // 停止心跳
    stopHeartbeat()
    
    // 如果是手动断开，不重连
    if (isManualDisconnect) {
      return
    }
    
    // 如果不是服务器主动断开（如认证失败），尝试重连
    if (reason !== 'io server disconnect') {
      attemptReconnect()
    } else {
      // 服务器主动断开，可能是认证失败或心跳超时，要求重新登录
      // console.warn('[心跳] 服务器主动断开连接，可能是心跳超时或认证失败')
      handleReconnectFailed('服务器断开连接，请重新登录')
    }
  })
  
  // 连接错误
  socket.on('connect_error', (error) => {
    console.error('Socket 连接错误:', error)
    // connect_error 后会自动触发 disconnect，在那里处理重连
  })
  
  return socket
}

// 尝试重连
function attemptReconnect() {
  const userStore = useUserStore()
  
  // 如果没有token，不重连
  if (!userStore.token) {
    return
  }
  
  reconnectAttempts++
  console.log(`尝试重连 (${reconnectAttempts}/${maxReconnectAttempts})...`)
  
  // 触发重连中事件
  window.dispatchEvent(new CustomEvent('socket:reconnecting', { 
    detail: { attempts: reconnectAttempts, max: maxReconnectAttempts } 
  }))
  
  if (reconnectAttempts >= maxReconnectAttempts) {
    // 先尝试切换节点（reportFailure 内部会触发 server:switched 事件）
    console.warn('[Socket] 重连次数耗尽，尝试切换服务器节点...')
    reportFailure().then(() => {
      // server:switched 事件监听器会自动重连，如果 _nodeSwitched 还是 false
      // 说明已无可用节点，才真正报错
      if (!_nodeSwitched) {
        handleReconnectFailed('连接失败，请重新登录')
      }
    })
    return
  }
  
  // 延迟重连（指数退避）
  const delay = Math.min(1000 * Math.pow(2, reconnectAttempts - 1), 10000)
  reconnectTimer = setTimeout(() => {
    if (socket && !socket.connected && !isManualDisconnect) {
      socket.connect()
    }
  }, delay)
}

// 处理重连失败
function handleReconnectFailed(message) {
  console.error('WebSocket重连失败:', message)
  
  // 断开连接
  if (socket) {
    socket.removeAllListeners()
    socket.disconnect()
    socket = null
  }
  
  // 清除重连定时器
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
  
  // 触发重连失败事件
  window.dispatchEvent(new CustomEvent('socket:reconnect_failed', { 
    detail: { message } 
  }))
  
  // 要求重新登录
  const userStore = useUserStore()
  if (userStore.token) {
    // 延迟一下，让事件监听器有时间处理
    setTimeout(() => {
      userStore.logout()
      // 触发需要重新登录的事件
      window.dispatchEvent(new CustomEvent('socket:require_relogin', { 
        detail: { message: '连接失败，请重新登录' } 
      }))
    }, 1000)
  }
}

// 启动心跳（每10秒发送一次）
function startHeartbeat() {
  // 清除旧的心跳定时器
  stopHeartbeat()
  
  // console.log('[心跳启动] ✅ 开始启动心跳机制（每10秒发送一次）')
  // console.log('[心跳启动] Socket状态:', socket ? { connected: socket.connected, id: socket.id } : 'null')
  
  // 检查 socket 是否存在且连接
  if (!socket) {
    // console.error('[心跳启动] ❌ Socket 不存在，无法启动心跳')
    return
  }
  
  if (!socket.connected) {
    // console.warn('[心跳启动] ⚠️ Socket 未连接，延迟启动心跳')
    // 等待连接后再启动
    const checkConnection = setInterval(() => {
      if (socket && socket.connected) {
        clearInterval(checkConnection)
        // console.log('[心跳启动] ✅ Socket 已连接，继续启动心跳')
        startHeartbeat()
      } else if (!socket) {
        clearInterval(checkConnection)
        // console.error('[心跳启动] ❌ Socket 已销毁，取消启动心跳')
      }
    }, 1000)
    
    // 10秒后如果还没连接，取消检查
    setTimeout(() => {
      clearInterval(checkConnection)
    }, 10000)
    return
  }
  
  // console.log('[心跳启动] ✅ Socket 已连接，立即发送第一次心跳')
  
  // 立即发送一次心跳
  sendHeartbeat()
  
  // 每10秒发送一次心跳
  heartbeatInterval = setInterval(() => {
    // console.log('[心跳定时器] ⏰ 定时器触发，准备发送心跳，当前时间:', new Date().toISOString())
    sendHeartbeat()
  }, 10000) // 10秒
  
  // console.log('[心跳启动] ✅ 心跳定时器已启动，interval ID:', heartbeatInterval)
  
  // 监听页面可见性变化，确保在页面切换到前台时立即发送心跳
  document.addEventListener('visibilitychange', handleVisibilityChange)
  // console.log('[心跳启动] ✅ 已添加页面可见性监听器')
}

// 处理页面可见性变化
function handleVisibilityChange() {
  if (!document.hidden && socket && socket.connected) {
    // 页面切换到前台，立即发送心跳
    // console.log('[心跳] 页面切换到前台，立即发送心跳')
    sendHeartbeat()
  }
}

// 停止心跳
function stopHeartbeat() {
  if (heartbeatInterval) {
    // console.log('[心跳停止] 正在停止心跳机制，interval ID:', heartbeatInterval)
    clearInterval(heartbeatInterval)
    heartbeatInterval = null
    // console.log('[心跳停止] ✅ 心跳机制已停止')
  } else {
    // console.log('[心跳停止] 心跳机制未运行，无需停止')
  }
  
  // 移除页面可见性监听器
  document.removeEventListener('visibilitychange', handleVisibilityChange)
}

// 发送心跳
function sendHeartbeat() {
  if (!socket) {
    // console.warn('[心跳发送] ⚠️ Socket 不存在，无法发送心跳')
    return
  }
  
  if (!socket.connected) {
    // console.warn('[心跳发送] ⚠️ Socket 未连接，无法发送心跳，Socket ID:', socket.id)
    return
  }
  
  try {
    const timestamp = Date.now()
    const timeString = new Date(timestamp).toISOString()
    
    socket.emit('heartbeat', {
      timestamp: timestamp
    })
    
    // console.log(`[心跳发送] ✅ 发送心跳包成功，时间戳: ${timestamp}，时间: ${timeString}，Socket ID: ${socket.id}`)
  } catch (error) {
    // console.error('[心跳发送] ❌ 发送心跳包失败:', error, 'Socket ID:', socket?.id)
  }
}

// 断开 Socket 连接
export function disconnectSocket() {
  isManualDisconnect = true
  
  // 停止心跳
  stopHeartbeat()
  
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
  
  if (socket) {
    socket.removeAllListeners()
    socket.disconnect()
    socket = null
  }
  
  reconnectAttempts = 0
}

// 获取 Socket 实例
export function getSocket() {
  return socket
}

// 发送消息
export function emit(event, data) {
  if (socket && socket.connected) {
    socket.emit(event, data)
  } else {
    console.warn('Socket 未连接，无法发送消息')
  }
}

// 监听事件
export function on(event, callback) {
  if (socket) {
    socket.on(event, callback)
  }
}

// 取消监听事件
export function off(event, callback) {
  if (socket) {
    socket.off(event, callback)
  }
}

